import {computed, inject, Injectable, signal} from "@angular/core";
import {takeUntilDestroyed, toObservable} from "@angular/core/rxjs-interop";
import {Router} from "@angular/router";
import {
  BehaviorSubject,
  catchError, combineLatest, concatMap, finalize,
  forkJoin,
  map,
  Observable,
  of, startWith,
  Subject,
  switchMap,
  tap
} from "rxjs";
import {ApiService, Tables} from "src/app/services/api.service";
import {MalfunctionStatus, Report} from "src/app/shared/interfaces/report";
import {IncidentIdService} from "./incident-id.service";
import {UserService} from "src/app/services/user.service";
import {CookieService} from "ngx-cookie-service";
import {EventService} from "src/app/services/event.service";
import {Userrole} from "src/app/shared/interfaces/user";
import {Escalation, EscalationLog, EscalationStatus} from "../../escalation/escalation.model";
import {SolutionService} from "../../knowledge-database/data-access/solution.service";
import {LocalStorageService} from "../../../shared/data-access/local-storage.service";
import {
  EscalationLoggingService,
} from "../../escalation/data-access/escalation-logging.service";
import {Database} from "../../../../../database.types";
import {NavigationService} from "../../../shared/data-access/navigation.service";
import {
  LocalSupabaseEdgeFunctionService,
  NotificationResponse,
  ReportDetails
} from "../../../services/local-supabase-edge-function.service";
import {environment} from "../../../../environments/environment";
import {TreeNode} from "../../knowledge-database/kd-workplace/kd-workplace.component";
import {NotificationService} from "../../../shared/data-access/notification.service";

export interface EscalationDetails {
  targetRole: Userrole;
  message: string;
  selectedValues: {
    productionLine: TreeNode;
    workplace: TreeNode;
    category: TreeNode;
  }
}


interface ReportState {
  currentReport: Partial<Report> | null;
  reportBeingUpdated: string;
  latestReports: Report[];
  escalatedReports: Report[];
}

@Injectable({
  providedIn: "root",
})
export class ReportService {
  apiService = inject(ApiService);
  cookieService = inject(CookieService);
  localStorageService = inject(LocalStorageService)
  eventService = inject(EventService);
  solutionService = inject(SolutionService)
  incidentIdService = inject(IncidentIdService);
  userService = inject(UserService);
  escalationLoggingService = inject(EscalationLoggingService);
  notificationService = inject(NotificationService);
  localEdgeFunctionService = inject(LocalSupabaseEdgeFunctionService);
  router = inject(Router);
  navigationService = inject(NavigationService);

  userrole = Userrole;

  // sources
  currentReport$ = new BehaviorSubject<Partial<Report> | null>(null);
  reportClosed = signal<boolean>(false); // prevent updates after report is closed
  add$ = new Subject<Partial<Report>>();
  update$ = new Subject<{
    properties: Partial<Report>
  }>();
  escalateReport$ = new Subject<EscalationDetails>();
  cancelReport$ = new Subject<void>();
  updateEscalation$ = new Subject<Database['public']['Tables']['Escalation']['Update']>()
  reportBeingUpdated$ = new Subject<string>();
  // addNewSolution$ = new Subject<Partial<Solution>>();
  incidentId$ = this.incidentIdService.incidentId$;
  private refreshTrigger$ = signal(0);
  isSendingEmailNotification = signal(false);
  private escalatedReports$: Observable<Report[]> = toObservable(
    computed(() => {
      this.refreshTrigger$();
      if (this.userService.user()) {
        return this.apiService.getActiveEscalations(
          this.userService.user()!)
      } else {
        return of([])
      }
    })
  ).pipe(
    switchMap((reports) => reports.pipe(map((reportData) => reportData))),
    startWith([]),
    takeUntilDestroyed()
  )
  private latestReports$ = toObservable(
    computed(() => {
      this.refreshTrigger$();
      return this.getLatestReports();
    })
  ).pipe(
    switchMap((response) => response.pipe(map(({reportData}) => reportData))),
    startWith([]),
    takeUntilDestroyed()
  );
  private filteredLatestReports$ = combineLatest([
    this.latestReports$,
    this.escalatedReports$
  ]).pipe(
    map(([latestReports, escalatedReports]) => {

      const safeLatestReports = Array.isArray(latestReports) ? latestReports : [];
      const safeEscalatedReports = Array.isArray(escalatedReports) ? escalatedReports : [];

      // Assuming reports have an 'id' field to identify them
      const escalatedIds = new Set(safeEscalatedReports.map(report => report.id));
      return safeLatestReports.filter(report => report && report.id && !escalatedIds.has(report.id));
    }),
    catchError(error => {
      console.error('Error processing reports:', error)
      return of([])
    })
  );


  public resetStateOnLogout$ = this.eventService.logout$;

  initialState = {
    currentReport: null,
    reportBeingUpdated: "",
    latestReports: [],
    escalatedReports: []
  };

  // state
  private state = signal<ReportState>(this.initialState);

  // selector
  currentReport = computed(() => this.state().currentReport);
  reportBeingUpdated = computed(() => this.state().reportBeingUpdated);
  latestActiveReports = computed(() =>
    this.state().latestReports?.filter(
      (report) =>
        report.status === MalfunctionStatus.RECORDED || report.status === MalfunctionStatus.ESCALATED
    )
  );
  // Show five latest solved reports
  latestSolvedReports = computed(() =>
    this.state()
      .latestReports?.filter(
      (report) => report.status === MalfunctionStatus.SOLVED
    )
      .slice(0, 5)
  );
  escalatedReports = computed(() => {
    return [...(this.state().escalatedReports ?? [])]
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
  })

  constructor() {
    this.filteredLatestReports$.pipe(takeUntilDestroyed()).subscribe((latestReports) =>
      this.state.update((state) => ({
        ...state,
        latestReports: latestReports,
      }))
    );

    this.escalatedReports$.pipe(takeUntilDestroyed()).subscribe((escalatedReports) =>
      this.state.update((state) => ({
        ...state,
        escalatedReports,
      }))
    );

    this.currentReport$.pipe(takeUntilDestroyed()).subscribe((report) => {
        this.state.update((state) => ({
          ...state,
          currentReport: report,
        }))
      }
    );

    this.add$.pipe(takeUntilDestroyed()).subscribe((report) =>
      this.state.update((state) => ({
        ...state,
        currentReport: report,
      }))
    );

    this.incidentId$.pipe(takeUntilDestroyed()).subscribe((incidentId) => {
      this.state.update((state) => {
        if (!this.currentReport) {
          return state;
        }

        return {
          ...state,
          currentReport: {
            ...state.currentReport,
            incident_id: incidentId,
          },
        };
      });
    });

    this.update$
      .pipe(
        // filter((updates) => !!updates.properties && Object.keys(updates.properties).length > 0),
        concatMap((updates) => {
          console.log('updating report', updates);
          return this.updateReport(updates).pipe(
            tap((updatedReport) => {
              if (updatedReport && !this.reportClosed()) {
                console.log('API Response', updatedReport)
                this.state.update((state) => ({
                  ...state,
                  currentReport: updatedReport
                }))
              } else if (this.reportClosed()) {
                console.log('report closed:', updatedReport);
              } else {
                console.warn('API returned null', updatedReport);
              }
            }),
          )
        }),
        takeUntilDestroyed(),
      ).subscribe({
        next: (updatedReport) => {
          if (!this.reportClosed()) {
            console.log('Report updated successfully', updatedReport)
            this.refreshReports()
          }
        },
        error: (err) => {
          console.error("Update failed", err);
        }
      }
    );

    this.escalateReport$
      .pipe(
        switchMap((escalationDetails) => this.escalateReport().pipe(
          map((report) => ({escalationDetails, report}))
        )),
        switchMap(({
                     escalationDetails,
                     report
                   }) => this.addNewEscalation(report, escalationDetails).pipe(
          map(({escalation}) => ({escalationDetails, report, escalation})))
        ),
        switchMap(({
                     escalationDetails,
                     report,
                     escalation
                   }) => this.sendNotification(escalationDetails, report, escalation)),

        takeUntilDestroyed(),
      ).subscribe(() => {
      this.notificationService.showNotification(
        'Die Stillstandsmeldung wurde erfolgreich weitergeleitet'
      )
      this.clearStateAfterClosingReport()
    })

    this.cancelReport$.pipe(takeUntilDestroyed()).subscribe(() => {
      this.update$.next({
        properties: {
          status: MalfunctionStatus.CANCELED
        }
      })
      this.clearStateAfterClosingReport();
    })

    this.reportBeingUpdated$.pipe(takeUntilDestroyed()).subscribe((reportId) =>
      this.state.update((state) => ({
        ...state,
        reportBeingUpdated: reportId,
      }))
    );

    /*this.addNewSolution$.pipe(
      map((newSolution) => this.addNewSolution(newSolution)),
      takeUntilDestroyed()
    ).subscribe();*/

    this.resetStateOnLogout$.pipe(takeUntilDestroyed()).subscribe(() => {
      this.resetState();
    });
  }

  initializeLatestReports() {
    this.filteredLatestReports$.pipe(takeUntilDestroyed()).subscribe((latestReports) =>
      this.state.update((state) => ({
        ...state,
        latestReports: latestReports,
      }))
    );
  }

  initializeEscalatedReports() {
    this.escalatedReports$.pipe(takeUntilDestroyed()).subscribe((escalatedReports) =>
      this.state.update((state) => ({
        ...state,
        escalatedReports,
      }))
    );
  }

  refreshReports(): void {
    this.refreshTrigger$.update((val) => val + 1);
  }

  resetState(propertiesToExclude: (keyof ReportState)[] = []): void {
    const preservedValues = Object.fromEntries(
      propertiesToExclude.map((prop) => [prop, this.state()[prop]])
    );

    this.state.set({
      ...this.initialState,
      ...preservedValues,
    });
  }

  getLatestReports(): Observable<any> {
    switch (this.userService.userrole()) {
      case Userrole.Mitarbeiter:
        return this.apiService.getFilteredReports({
          workplaceFilter: Array(
            this.cookieService.get("workplace").split("%")[0] || ""
          ),
        });
      case Userrole.Teamleiter:
      case Userrole.Schichtleiter:
        return this.apiService.getFilteredReports({
          productionLineFilter: Array(
            this.userService
              .user()
              ?.app_metadata?.["productionLine"].split("%")[0]
          ),
        });

      case Userrole.Produktionsleiter:
      case Userrole.Techniker:
        return this.apiService.getFilteredReports();
      default:
        return of([]);
    }
  }

  addNewReport(report: Partial<Report>): Observable<Report> {
    return this.apiService.addAndReturnItem<Partial<Report>, Report>(Tables.Report, report)
  }


  updateReport(updates: {
    properties: Partial<Report>;
  }): Observable<Report | null> {
    if (this.currentReport()?.id) {
      return this.apiService.updateAndReturnItem(Tables.Report, updates.properties, {
        key: 'id',
        value: this.currentReport()?.id!
      })
    } else return of(null)
  }

  solveEscalatedReport(resolveEscalationObject: { report: Report, solutionId: string }): void {
    // close escalation if exists
    if (resolveEscalationObject.report.status === MalfunctionStatus.ESCALATED) {
      this.updateEscalation$.next({
        report: resolveEscalationObject.report.id,
        status: EscalationStatus.COMPLETED
      })
    }

    this.apiService.updateAndReturnItem(
      Tables.Report,
      {
        id: resolveEscalationObject.report.id,
        solution: resolveEscalationObject.solutionId,
        solved_on: new Date().toISOString(),
        solved_by: this.userService.user()?.id,
        status: MalfunctionStatus.SOLVED,
      },
      {
        key: 'id',
        value: resolveEscalationObject.report.id
      }
    ).subscribe((updatedReport) => {
      console.log('report updated', updatedReport)
      this.clearStateAfterClosingReport()

    })
  }

  /*solveReport(report: Report, solutionId: string): Observable<Report> {
    // close escalation if exists
    if (report.status === MalfunctionStatus.ESCALATED) {
      this.updateEscalation$.next({
        report: report.id,
        status: EscalationStatus.COMPLETED
      })
    }

    return this.apiService.updateAndReturnItem(
      Tables.Report,
      {
        id: report.id,
        solution: solutionId,
        solved_on: new Date().toISOString(),
        solved_by: this.userService.user()?.id,
        status: MalfunctionStatus.SOLVED,
      },
      {
        key: 'id',
        value: report.id
      }
    );
  }*/

  solveActiveReport() {
    this.update$.next({
      properties: {
        solved_by: this.userService.user()!.id,
        status: MalfunctionStatus.SOLVED,
        solved_on: new Date().toISOString()
      }
    })
    this.clearStateAfterClosingReport()
  }

  clearStateAfterClosingReport() {
    this.currentReport$.next(null);
    this.reportClosed.set(true);
    this.solutionService.resetState();
    this.localStorageService.removeFromLocalStorage('currentReport')
    this.navigationService.allowNavigation();
    this.router.navigateByUrl('/dashboard');
  }

  escalateReport(): Observable<Report> {
    this.update$.next({
      properties: {
        solution: this.solutionService.solution().id,
        status: MalfunctionStatus.ESCALATED
      }
    })
    return of(this.currentReport() as Report);
  }

  addNewEscalation(report: Report, escalationDetails: EscalationDetails): Observable<{
    escalation: Escalation,
    escalationLog: EscalationLog
  }> {
    const escalation: Omit<Escalation, 'id' | 'assigned_to_user'> = {
      created_at: new Date().toISOString(),
      created_by: report.created_by,
      report: report.id,
      assigned_to_role: escalationDetails.targetRole,
      status: EscalationStatus.CREATED,
      message: escalationDetails.message,
      processing_status_notification: null
    }

    return this.apiService.addAndReturnItem<Omit<Escalation, 'id' | 'created_at' | 'assigned_to_user'>, Escalation>(Tables.Escalation, escalation).pipe(
      switchMap((escalation: Escalation) => this.escalationLoggingService.logEscalation({
          id: escalation.id,
          reportId: escalation.report!,
          assignedToUser: escalation.assigned_to_user ? escalation.assigned_to_user : null,

        }, 'create').pipe(map((escalationLog) => {
            return {escalation, escalationLog}
          })
        )
      )
    )
  }

  sendNotification(escalationDetails: EscalationDetails, report: Report, escalation: Escalation): Observable<EscalationLog | EscalationLog[]> {
    this.isSendingEmailNotification.set(true);
    const reportDetails: ReportDetails = {
      category: escalationDetails.selectedValues.category.name,
      created_at: new Date(report.created_at).toLocaleString(),
      id: report.id,
      incidentId: report.incident_id!,
      link: `${environment.applicationUrl}/report/${report.id}`,
      message: escalationDetails.message,
      productionLine: escalationDetails.selectedValues.productionLine!.id,
      productionLineName: escalationDetails.selectedValues.productionLine!.name,
      workplace: escalationDetails.selectedValues.workplace!.name
    }
    return this.apiService.sendEscalationEmail(escalationDetails.targetRole, reportDetails).pipe(
      switchMap((functionResponse: NotificationResponse) => {
        if (functionResponse.recipientCount == 0) {
          return this.escalationLoggingService.logEscalation({
            id: escalation.id,
            reportId: report.id!,
            assignedToUser: null,
          }, 'emailNotification')
        } else {
          return forkJoin(functionResponse.recipients.map(user =>
            this.escalationLoggingService.logEscalation({
              id: escalation.id,
              reportId: report.id!,
              assignedToUser: user.id,
            }, 'emailNotification')
          ))
        }
      }),
      catchError(error => {
        console.log(typeof error, error);
        console.log(escalation);
        this.notificationService.showNotification('E-Mail Benachrichtigung konnte nicht gesendet werden');
        return this.escalationLoggingService.logEscalation({
          id: escalation.id,
          reportId: report.id!,
          assignedToUser: null,
          errorMessage: error.message
        }, 'error')
      }),
      finalize(() => this.isSendingEmailNotification.set(false))
    )
  }


  getActiveEscalation(reportId: string): Observable<Escalation> {
    return this.apiService.getActiveEscalation({
      relation: Tables.Escalation,
      identifierKey: 'report',
      identifierValue: reportId
    })
  }


  /*addNewSolution(newSolution: Partial<Solution>) {
    return this.apiService
      .addItemToKnowledgeBase(Tables.Solution, {
        name: newSolution.name,
        description: newSolution.description,
        malfunction: this.knowledgeBaseService.expandedNodes().malfunction?.id,
      })
      .pipe(
        switchMap((solution) => {
          return this.apiService.updateItem(Tables.Report, {
            id: this.reportBeingUpdated(),
            solution: solution.id,
          });
        })
      )
      .subscribe(() => window.location.reload());
  }*/
}
